import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, Building2, Mail, UserPlus, Users } from "lucide-react"
import { useTranslation } from "react-i18next"

import { InviteFirstAdminDialog } from "@/components/admin/invite-first-admin-dialog"
import { PendingFirstAdminInviteCard } from "@/components/admin/pending-first-admin-invite-card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { nameOrDash } from "@/lib/admin-placeholder"
import { adminAgenciesPath, adminBrandPath } from "@/routes"
import type { AgencyShowProps } from "@/types/admin"

/**
 * Admin/Agencies/Show — read-only agency detail (OKS-15 AC3 detail).
 *
 * No mutable region — agency assignments are set from the brand side
 * (see `Admin/Brands/Show` Assignment card). The agency-side view shows
 * the rolled-up state plus the per-brand history rows.
 */
export default function AdminAgenciesShow({
  agency,
  team,
  active_brands,
  assignment_history,
  pending_first_admin_invite,
  can_invite_first_admin,
}: AgencyShowProps) {
  const { t, i18n } = useTranslation()

  const dateFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
  const dateTimeFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  })

  return (
    <AppLayout>
      <Head title={agency.name} />
      <div
        className="flex flex-col gap-6 p-6"
        data-testid="admin-agency-detail"
        data-agency-id={agency.id}
      >
        <header className="flex flex-col gap-2">
          <Link
            href={adminAgenciesPath()}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("admin.agency_detail.back")}
          </Link>
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {agency.name}
          </h1>
        </header>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{t("admin.agency_detail.cards.profile")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              <Row
                label={t("admin.agency_detail.profile.slug")}
                value={agency.slug}
              />
              {agency.locale && (
                <Row
                  label={t("admin.agency_detail.profile.locale")}
                  value={agency.locale.toUpperCase()}
                />
              )}
              <Row
                label={t("admin.agency_detail.profile.created_at")}
                value={dateFmt.format(new Date(agency.created_at))}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                {t("admin.agency_detail.cards.team")}{" "}
                <span className="text-muted-foreground ml-1 text-sm font-normal">
                  ({team.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {team.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t("admin.agency_detail.team_empty")}
                </p>
              ) : (
                <ul className="flex flex-col gap-3" role="list">
                  {team.map((member) => (
                    <li key={member.id} className="flex items-start gap-3">
                      <Users
                        className="text-muted-foreground mt-0.5 size-4 shrink-0"
                        aria-hidden="true"
                      />
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-foreground truncate text-sm font-medium">
                          {member.name}
                        </span>
                        <span className="text-muted-foreground inline-flex items-center gap-1 truncate text-xs">
                          <Mail className="size-3" aria-hidden="true" />
                          {member.email}
                        </span>
                      </div>
                      <Badge variant="secondary" className="ml-auto shrink-0">
                        {member.role}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/*
           * OKS-12 Pending first-admin invite Card. Same component +
           * branching as Brands/Show; tenantKind="agency" picks the
           * agency-flavoured empty-state subtitle. `onInviteFirstAdmin`
           * is a no-op TODO until backend ships an invite-to-existing-
           * tenant path; `canInvite` is server-gated so the CTA hides
           * until that path lands.
           */}
          <div className="lg:col-span-2">
            <PendingFirstAdminInviteCard
              tenantId={agency.id}
              tenantKind="agency"
              viewer="admin"
              invite={pending_first_admin_invite}
              teamSize={team.length}
              inviteAction={
                can_invite_first_admin ? (
                  <InviteFirstAdminDialog
                    tenant={{
                      kind: "agency",
                      id: agency.id,
                      name: agency.name,
                    }}
                    trigger={
                      <Button
                        type="button"
                        data-testid="admin-pending-first-admin-empty-cta"
                      >
                        <UserPlus className="size-4" aria-hidden="true" />
                        {t("admin.invites.card.empty.cta")}
                      </Button>
                    }
                  />
                ) : undefined
              }
            />
          </div>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>
                {t("admin.agency_detail.cards.active_brands")}{" "}
                <span className="text-muted-foreground ml-1 text-sm font-normal">
                  ({active_brands.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {active_brands.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t("admin.agency_detail.active_brands_empty")}
                </p>
              ) : (
                <ul
                  className="flex flex-wrap gap-2"
                  role="list"
                  data-testid="admin-agency-active-brands"
                >
                  {active_brands.map((b) => (
                    <li key={b.id}>
                      <Link
                        href={adminBrandPath({ id: b.id })}
                        className="bg-muted/50 hover:bg-muted inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm"
                      >
                        <Building2
                          className="text-muted-foreground size-4"
                          aria-hidden="true"
                        />
                        <span className="font-medium">
                          {nameOrDash(b.name)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>{t("admin.agency_detail.cards.history")}</CardTitle>
            </CardHeader>
            <CardContent>
              {assignment_history.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t("admin.agency_detail.history_empty")}
                </p>
              ) : (
                <ul
                  className="flex flex-col gap-2"
                  role="list"
                  data-testid="admin-agency-assignment-history"
                >
                  {assignment_history.map((entry) => {
                    const isActive = entry.ended_at === null
                    return (
                      <li
                        key={`${entry.brand.id}-${entry.starts_at}`}
                        className="flex items-start gap-3 text-sm"
                      >
                        <span
                          aria-hidden="true"
                          className={
                            isActive
                              ? "bg-primary mt-1.5 size-2 shrink-0 rounded-full"
                              : "bg-muted-foreground/40 mt-1.5 size-2 shrink-0 rounded-full"
                          }
                        />
                        <div className="flex min-w-0 flex-col">
                          <Link
                            href={adminBrandPath({ id: entry.brand.id })}
                            className="text-foreground font-medium hover:underline"
                          >
                            {nameOrDash(entry.brand.name)}
                          </Link>
                          <span className="text-muted-foreground text-xs">
                            {dateTimeFmt.format(new Date(entry.starts_at))}
                            {entry.ended_at && (
                              <>
                                {" → "}
                                {dateTimeFmt.format(new Date(entry.ended_at))}
                              </>
                            )}
                            {" · "}
                            {t("admin.agency_detail.history_assigned_by", {
                              actor: entry.assigned_by.name,
                            })}
                          </span>
                        </div>
                        {isActive && (
                          <Badge variant="secondary" className="ml-auto">
                            {t("admin.agency_detail.history_active")}
                          </Badge>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-muted-foreground w-32 shrink-0">{label}</span>
      <span className="text-foreground truncate font-medium">{value}</span>
    </div>
  )
}
